import { useEffect, useRef, useState } from "react";
import { track } from "@/lib/api";

function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export default function QuoteCard({ quotes = [] }) {
  const canvasRef = useRef(null);
  const [quoteId, setQuoteId] = useState(quotes[0]?.quote_id || "");
  const [format, setFormat] = useState("square");
  const quote = quotes.find((q) => q.quote_id === quoteId) || quotes[0];

  useEffect(() => {
    if (!quote || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const W = 1080;
    const H = format === "square" ? 1080 : 1350;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#080808";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(197,155,39,0.4)";
    ctx.lineWidth = 2;
    ctx.strokeRect(48, 48, W - 96, H - 96);
    ctx.fillStyle = "#B71F32";
    ctx.fillRect(48, 48, W - 96, 6);

    let fontSize = 54;
    ctx.textAlign = "center";
    let lines = [];
    while (fontSize > 28) {
      ctx.font = `italic 600 ${fontSize}px "Cormorant Garamond", Georgia, serif`;
      lines = wrapText(ctx, `\u201C${quote.text}\u201D`, W - 220);
      if (lines.length * fontSize * 1.4 < H * 0.5) break;
      fontSize -= 4;
    }
    ctx.fillStyle = "#F2EDE5";
    const lineH = fontSize * 1.4;
    let y = H / 2 - ((lines.length - 1) * lineH) / 2 - 40;
    for (const ln of lines) {
      ctx.fillText(ln, W / 2, y);
      y += lineH;
    }

    ctx.font = '600 24px Inter, sans-serif';
    ctx.fillStyle = "#B71F32";
    const srcY = y + 10;
    if (quote.source) ctx.fillText(quote.source.toUpperCase(), W / 2, srcY);

    ctx.font = '700 30px Cinzel, serif';
    ctx.fillStyle = "#F2EDE5";
    ctx.fillText("DIRTY CHURCH CHRONICLES", W / 2, H - 170);
    ctx.font = '400 22px Inter, sans-serif';
    ctx.fillStyle = "rgba(242,237,229,0.65)";
    ctx.fillText("Volume I \u00B7 Jermaine Miller", W / 2, H - 128);
    ctx.fillStyle = "#C59B27";
    ctx.fillText("dirtychurchchronicles.com", W / 2, H - 88);
  }, [quote, format]);

  const download = () => {
    const a = document.createElement("a");
    a.download = `dcc-quote-${format}.png`;
    a.href = canvasRef.current.toDataURL("image/png");
    a.click();
    track("quote_download", format);
  };

  if (!quotes.length) return null;

  return (
    <div data-testid="quote-card-generator" className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
      <div className="border border-paper/10 bg-coal p-4">
        <canvas ref={canvasRef} className="w-full h-auto" data-testid="quote-canvas" />
      </div>
      <div>
        <label htmlFor="quote-select" className="eyebrow block mb-3">Choose a quotation</label>
        <select
          id="quote-select"
          value={quoteId}
          onChange={(e) => setQuoteId(e.target.value)}
          data-testid="quote-select"
          className="w-full bg-ink border border-paper/20 px-4 py-3 font-ui text-sm text-paper mb-8"
        >
          {quotes.map((q) => (
            <option key={q.quote_id} value={q.quote_id}>
              {q.source || "Quotation"} — {q.text.slice(0, 60)}{q.text.length > 60 ? "…" : ""}
            </option>
          ))}
        </select>
        <p className="eyebrow mb-3">Format</p>
        <div className="flex gap-3 mb-8">
          <button
            onClick={() => setFormat("square")}
            data-testid="quote-format-square"
            className={`font-ui text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-colors duration-300 ${
              format === "square" ? "bg-crimson border-crimson text-paper" : "border-paper/25 text-paper/70 hover:border-paper/60"
            }`}
          >
            Square 1:1
          </button>
          <button
            onClick={() => setFormat("portrait")}
            data-testid="quote-format-portrait"
            className={`font-ui text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-colors duration-300 ${
              format === "portrait" ? "bg-crimson border-crimson text-paper" : "border-paper/25 text-paper/70 hover:border-paper/60"
            }`}
          >
            Portrait 4:5
          </button>
        </div>
        <button
          onClick={download}
          data-testid="quote-download-btn"
          className="font-ui text-xs uppercase tracking-[0.25em] font-semibold bg-paper text-ink px-8 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-white hover:scale-[1.02] active:scale-[0.98]"
        >
          Download Quote Card
        </button>
      </div>
    </div>
  );
}
